export type ASTNode = {
  type: 'BinaryExpression' | 'Literal' | 'Identifier'
  operator?: string
  left?: ASTNode
  right?: ASTNode
  value?: string
}

export class Parser {
  private tokens: string[]
  private current = 0

  constructor(expression: string) {
    this.tokens = this.tokenize(expression)
  }

  private tokenize(expression: string): string[] {
    const tokens = ['(', ')', '||', '&', '=', '!=', '<', '>', '>=', '<=']

    const result: string[] = []
    let index = 0
    let literal = ''

    outer: while (index < expression.length) {
      for (const token of tokens) {
        const slice = expression.slice(index, index + token.length)

        if (slice === token) {
          const trimmedLiteral = literal.trim()

          if (trimmedLiteral.length > 0) {
            result.push(trimmedLiteral)
            literal = ''
          }

          result.push(slice)
          index += token.length

          continue outer
        }
      }

      literal += expression[index]
      index += 1
    }

    const trimmedLiteral = literal.trim()

    if (trimmedLiteral.length > 0) {
      result.push(trimmedLiteral)
      literal = ''
    }

    return result
  }

  private peek(): string | undefined {
    return this.tokens[this.current]
  }

  private consume(): string | undefined {
    return this.tokens[this.current++]
  }

  private expect(expected: string): void {
    if (this.peek() !== expected) {
      throw new Error(`Expected '${expected}', but found '${this.peek()}'`)
    }

    this.consume()
  }

  private parsePrimary(): ASTNode {
    const token = this.consume()

    if (!token) {
      throw new Error('Unexpected end of input')
    }

    if (token === '(') {
      const expr = this.parseBinaryExpression()

      this.expect(')')

      return expr
    }

    if (this.peek() === '=') {
      return { type: 'Identifier', value: token }
    }

    return { type: 'Literal', value: token }
  }

  private parseBinaryExpression(precedence = 0): ASTNode {
    let left = this.parsePrimary()

    while (true) {
      const operator = this.peek()

      if (!operator || !this.getPrecedence(operator) || this.getPrecedence(operator) < precedence) {
        break
      }

      this.consume()

      const right = this.parseBinaryExpression(this.getPrecedence(operator) + 1)

      left = {
        type: 'BinaryExpression',
        operator,
        left,
        right,
      }
    }

    return left
  }

  private getPrecedence(operator: string): number {
    switch (operator) {
      case '||':
        return 1
      case '&':
        return 2
      case '=':
      case '!=':
      case '<':
      case '>':
      case '<=':
      case '>=':
        return 3
      default:
        return 0
    }
  }

  public parse(): ASTNode {
    const ast = this.parseBinaryExpression()

    if (this.current < this.tokens.length) {
      throw new Error('Unexpected tokens remaining')
    }

    return ast
  }
}
